import {
	CardHeader,
	CardTitle,
	CardContent,
	Button,
	Switch,
} from "@/components/primitives";
import { ArrowRight, ArrowLeft, Target, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export interface ContributingFactor {
	id: string;
	title: string;
	description: string;
	selected: boolean;
}

export interface Signal {
	id: string;
	title: string;
	description: string;
	selected: boolean;
}

export interface CorrectiveAction {
	id: string;
	title: string;
	description: string;
	selected: boolean;
}

export interface ActionLink {
	correctiveActionId: string;
	linkedFactors: string[];
	linkedSignals: string[];
}

interface LinkingStepProps {
	/**
	 * Current corrective actions to link
	 */
	actions: CorrectiveAction[];
	/**
	 * All contributing factors (filtered to selected ones)
	 */
	factors: ContributingFactor[];
	/**
	 * All signals (filtered to selected ones)
	 */
	signals: Signal[];
	/**
	 * Links between actions and factors/signals
	 */
	actionLinks: ActionLink[];
	/**
	 * Current action index being linked
	 */
	currentLinkingIndex: number;
	/**
	 * Callback to update action links
	 */
	onUpdateLinks: (links: ActionLink[]) => void;
	/**
	 * Callback to change current linking index
	 */
	onChangeIndex: (index: number) => void;
	/**
	 * Callback when linking is complete
	 */
	onContinue: () => void;
	/**
	 * Callback to save current action's links to database
	 */
	onSaveCurrentLinks: (actionId: string) => Promise<void>;
	/**
	 * Expanded items tracking (for showing full descriptions)
	 */
	expandedItems?: Record<string, boolean>;
	/**
	 * Callback to toggle expanded items
	 */
	onToggleExpanded?: (id: string) => void;
}

/**
 * Helper to get first sentence of text
 */
function getFirstSentence(text: string): string {
	if (!text) return "";
	const match = text.match(/[^.!?]+[.!?]?/);
	if (match) return match[0].trim();
	return text.split("\n")[0].trim();
}

/**
 * Helper to dedupe items by title
 */
function dedupeByTitle<T extends { title: string }>(items: T[]): T[] {
	const seen = new Set<string>();
	return items.filter((it) => {
		const key = (it.title || "").trim().toLowerCase();
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

export function LinkingStep({
	actions,
	factors,
	signals,
	actionLinks,
	currentLinkingIndex,
	onUpdateLinks,
	onChangeIndex,
	onContinue,
	onSaveCurrentLinks,
	expandedItems = {},
	onToggleExpanded = () => {},
}: LinkingStepProps) {
	const topRef = useRef<HTMLDivElement | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	// Whenever we move to the next/previous action, scroll to the top to emphasize new content
	useEffect(() => {
		// Prefer scrolling the component into view if possible
		if (topRef.current) {
			try {
				topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			} catch {}
		}
		// Also ensure window is at the top in case the container isn't scrollable
		try {
			window.scrollTo({ top: 0, behavior: "smooth" });
		} catch {}
	}, [currentLinkingIndex]);

	if (actionLinks.length === 0) {
		return (
			<>
				<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
					<div className="flex items-center justify-center gap-3">
						<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
							<Target className="h-5 w-5 text-white" />
						</div>
						<div>
							<CardTitle className="text-xl sm:text-2xl font-semibold">
								Linking Complete
							</CardTitle>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-6">
					<div className="text-center space-y-4">
						<p className="text-muted-foreground">
							No corrective actions to link.
						</p>
						<Button
							onClick={onContinue}
							className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
						>
							Continue
							<ArrowRight className="h-4 w-4 ml-2" />
						</Button>
					</div>
				</CardContent>
			</>
		);
	}

	const currentLink = actionLinks[currentLinkingIndex];
	const currentAction = actions.find(
		(a) => a.id === currentLink?.correctiveActionId,
	);
	const dedupeFactors = dedupeByTitle(factors);
	const dedupeSignals = dedupeByTitle(signals);
	const isExpanded = (id: string) => !!expandedItems[id];

	return (
		<>
			<div ref={topRef} />
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center justify-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
						<Target className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold text-center">
							Possible outcomes of implementing your actions
						</CardTitle>
					</div>
				</div>
			</CardHeader>

			<div className="w-full flex items-center justify-center my-4">
				<div className="w-2/3 max-w-[400px]">
					<div className="flex items-center justify-between mb-2">
						<div className="text-sm font-semibold">
							<span className="inline-flex items-center px-3 py-1 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-medium shadow-sm text-gray-900 dark:text-gray-100">
								Step {currentLinkingIndex + 1} of {actionLinks.length}
							</span>
						</div>
						<div className="text-xs text-muted-foreground">
							Link corrective actions to outcomes
						</div>
					</div>
					<div className="relative h-1.5 bg-gray-200 rounded-full overflow-hidden">
						<div
							className="absolute left-0 top-0 h-1.5 bg-green-500"
							style={{
								width: `${Math.round(((currentLinkingIndex + 1) / Math.max(1, actionLinks.length)) * 100)}%`,
							}}
						/>
					</div>
				</div>
			</div>

			<CardContent>
				{currentAction && (
					<div className="space-y-6 animate-in fade-in-0 slide-in-from-bottom-2">
						{/* Your corrective action header */}
						<div className="text-center">
							<div
								key={`green-box-${currentLinkingIndex}`}
								className="mt-2 p-4 rounded-lg border border-green-100 bg-green-50 dark:border-green-900 dark:bg-green-950 mx-auto animate-in fade-in-0 duration-1000"
								style={{ maxWidth: "720px" }}
							>
								<div className="font-semibold text-lg text-center text-gray-900 dark:text-gray-100">
									{currentAction.title}
								</div>
								{currentAction.description && (
									<p className="text-base text-gray-700 dark:text-gray-200 mt-2 text-center">
										{currentAction.description}
									</p>
								)}
							</div>
						</div>

						{/* Intent copy */}
						<div className="text-center mt-3">
							<h4 className="text-lg font-semibold">
								Implementing this action should reduce..
							</h4>
						</div>

						{/* Two column flow: Contributing Factors -> Signals */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="border rounded-lg p-4">
								<h4 className="font-medium mb-2">these Contributing Factors</h4>
								<p className="text-xs text-muted-foreground mb-3">
									Select which contributing factors are addressed by this action
								</p>
								<div className="space-y-2">
									{dedupeFactors.length === 0 && (
										<div className="text-sm text-gray-500">
											No contributing factors selected previously.
										</div>
									)}
									{dedupeFactors.map((factor) => (
										<div key={factor.id} className="flex items-start space-x-3">
											<div className="pt-1">
												<Switch
													checked={
														currentLink?.linkedFactors.includes(factor.id) ||
														false
													}
													onCheckedChange={(checked) => {
														const updatedLinks = actionLinks.map(
															(link, index) =>
																index === currentLinkingIndex
																	? {
																			...link,
																			linkedFactors: checked
																				? [...link.linkedFactors, factor.id]
																				: link.linkedFactors.filter(
																						(id) => id !== factor.id,
																					),
																		}
																	: link,
														);
														onUpdateLinks(updatedLinks);
													}}
												/>
											</div>
											<div>
												<div className="font-medium">{factor.title}</div>
												{factor.description && (
													<div className="text-sm text-gray-600">
														{isExpanded(factor.id)
															? factor.description
															: getFirstSentence(factor.description)}
														{factor.description &&
															factor.description.length >
																getFirstSentence(factor.description).length && (
																<button
																	className="ml-2 text-xs text-blue-600 hover:underline"
																	onClick={() => onToggleExpanded(factor.id)}
																>
																	{isExpanded(factor.id)
																		? "Show less"
																		: "Read more"}
																</button>
															)}
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							</div>

							<div className="border rounded-lg p-4">
								<h4 className="font-medium mb-2">.. and these Signals</h4>
								<p className="text-xs text-muted-foreground mb-3">
									Select which warning signals are likely to reduce if this
									action is taken
								</p>
								<div className="space-y-2">
									{dedupeSignals.length === 0 && (
										<div className="text-sm text-gray-500">
											No signals selected previously.
										</div>
									)}
									{dedupeSignals.map((signal) => (
										<div key={signal.id} className="flex items-start space-x-3">
											<div className="pt-1">
												<Switch
													checked={
														currentLink?.linkedSignals.includes(signal.id) ||
														false
													}
													onCheckedChange={(checked) => {
														const updatedLinks = actionLinks.map(
															(link, index) =>
																index === currentLinkingIndex
																	? {
																			...link,
																			linkedSignals: checked
																				? [...link.linkedSignals, signal.id]
																				: link.linkedSignals.filter(
																						(id) => id !== signal.id,
																					),
																		}
																	: link,
														);
														onUpdateLinks(updatedLinks);
													}}
												/>
											</div>
											<div>
												<div className="font-medium">{signal.title}</div>
												{signal.description && (
													<div className="text-sm text-gray-600">
														{isExpanded(signal.id)
															? signal.description
															: getFirstSentence(signal.description)}
														{signal.description &&
															signal.description.length >
																getFirstSentence(signal.description).length && (
																<button
																	className="ml-2 text-xs text-blue-600 hover:underline"
																	onClick={() => onToggleExpanded(signal.id)}
																>
																	{isExpanded(signal.id)
																		? "Show less"
																		: "Read more"}
																</button>
															)}
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						</div>

						{/* Navigation */}
						<div className="flex justify-between pt-4">
							<Button
								variant="outline"
								onClick={() =>
									onChangeIndex(Math.max(0, currentLinkingIndex - 1))
								}
								disabled={currentLinkingIndex === 0 || isSaving}
							>
								<ArrowLeft className="h-4 w-4 mr-2" />
								Previous
							</Button>

							{currentLinkingIndex < actionLinks.length - 1 ? (
								<Button
									onClick={async () => {
										setIsSaving(true);
										try {
											await onSaveCurrentLinks(currentLink.correctiveActionId);
											onChangeIndex(currentLinkingIndex + 1);
										} catch (error) {
											console.error("Error saving links:", error);
											// Continue to next step even if save fails (will retry later)
											onChangeIndex(currentLinkingIndex + 1);
										} finally {
											setIsSaving(false);
										}
									}}
									disabled={isSaving}
								>
									{isSaving ? (
										<>
											<Loader2 className="h-4 w-4 mr-2 animate-spin" />
											Saving...
										</>
									) : (
										<>
											Next
											<ArrowRight className="h-4 w-4 ml-2" />
										</>
									)}
								</Button>
							) : (
								<Button
									onClick={async () => {
										setIsSaving(true);
										try {
											await onSaveCurrentLinks(currentLink.correctiveActionId);
											onContinue();
										} catch (error) {
											console.error("Error saving links:", error);
											// Continue even if save fails (will retry later)
											onContinue();
										} finally {
											setIsSaving(false);
										}
									}}
									disabled={isSaving}
								>
									{isSaving ? (
										<>
											<Loader2 className="h-4 w-4 mr-2 animate-spin" />
											Saving...
										</>
									) : (
										<>
											Done
											<ArrowRight className="h-4 w-4 ml-2" />
										</>
									)}
								</Button>
							)}
						</div>
					</div>
				)}
			</CardContent>
		</>
	);
}
